import { motion } from "framer-motion";
import { EASE, useSafeInView } from "@/components/Reveal";

const SYSTEMS = ["Salesforce", "SAP", "ServiceNow", "Slack", "Jira", "Email", "Docs"];
const MECHANISMS = ["Context Graph", "Correlation Engine", "Change Observability"];
const OUTCOMES = ["AI Systems", "Agents", "Decisions"];

function Tier({ y, h, label, show, delay, children, testid }) {
  return (
    <motion.g
      initial={{ opacity: 0, y: 16 }}
      animate={show ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: EASE, delay }}
      data-testid={testid}
    >
      <text x="100" y={y - 12} fill="rgba(255,255,255,0.35)" fontSize="10" fontFamily="'JetBrains Mono', monospace" letterSpacing="2.5">
        {label}
      </text>
      <rect x="100" y={y} width="740" height={h} fill="#0C0C0C" stroke="rgba(255,255,255,0.16)" />
      {children}
    </motion.g>
  );
}

function Chip({ x, y, w, label, accent }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height="30" fill="none" stroke={accent ? "#00C0C8" : "rgba(255,255,255,0.22)"} strokeOpacity={accent ? 0.6 : 1} />
      <text x={x + w / 2} y={y + 19} textAnchor="middle" fill={accent ? "#00C0C8" : "rgba(255,255,255,0.75)"} fontSize="11.5" fontFamily="'IBM Plex Sans', sans-serif">
        {label}
      </text>
    </g>
  );
}

function FlowArrows({ y1, y2, show, delay }) {
  return [470, 620, 770].map((x, i) => (
    <motion.g key={x}
      initial={{ opacity: 0 }}
      animate={show ? { opacity: 1 } : {}}
      transition={{ duration: 0.6, delay: delay + i * 0.12 }}
    >
      <line x1={x} y1={y1} x2={x} y2={y2} stroke="rgba(255,255,255,0.3)" strokeWidth="1.1" />
      <path d={`M ${x - 4} ${y2 - 5} L ${x} ${y2} L ${x + 4} ${y2 - 5}`} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" />
      <circle r="3" fill="#00C0C8">
        <animateMotion dur="2.2s" begin={`${i * 0.55}s`} repeatCount="indefinite" path={`M ${x} ${y1} L ${x} ${y2}`} />
      </circle>
    </motion.g>
  ));
}

export default function ArchitectureDiagram() {
  const [ref, show] = useSafeInView();
  return (
    <div ref={ref} className="min-w-[720px]">
      <svg viewBox="0 0 940 430" className="h-auto w-full" data-testid="diagram-architecture">
        <Tier y={30} h={64} label="ENTERPRISE SYSTEMS" show={show} delay={0.1} testid="arch-tier-systems">
          {SYSTEMS.map((s, i) => (
            <Chip key={s} x={112 + i * 104} y={47} w={96} label={s} />
          ))}
        </Tier>

        <FlowArrows y1={94} y2={138} show={show} delay={0.7} />

        <Tier y={138} h={110} label="DOTIQ — INSTITUTIONAL INTELLIGENCE" show={show} delay={0.4} testid="arch-tier-dotiq">
          <rect x="100" y="138" width="740" height="110" fill="none" stroke="#00C0C8" strokeOpacity="0.3" />
          {MECHANISMS.map((m, i) => (
            <g key={m}>
              <Chip x={126 + i * 246} y={168} w={200} label={m} accent />
              <text x={126 + i * 246 + 100} y={224} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="9.5" fontFamily="'JetBrains Mono', monospace" letterSpacing="1.5">
                {["STRUCTURE", "RELATIONSHIPS", "DRIFT"][i]}
              </text>
              {i < 2 && (
                <path d={`M ${126 + i * 246 + 206} 183 L ${126 + (i + 1) * 246 - 8} 183`} stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
              )}
            </g>
          ))}
        </Tier>

        <FlowArrows y1={248} y2={292} show={show} delay={1.0} />

        <Tier y={292} h={64} label="OUTCOMES" show={show} delay={0.7} testid="arch-tier-outcomes">
          {OUTCOMES.map((o, i) => (
            <Chip key={o} x={126 + i * 246} y={309} w={200} label={o} />
          ))}
        </Tier>
      </svg>
    </div>
  );
}
